import { BaseHTMLElement, customElement, first, onEvent } from 'dom-native';

import type { HelpAgentAttachmentPreview } from '@/types/attachments';
import type { ChatImage, ConnectionStatus } from '@/types/chat';
import { DEFAULT_HELP_CENTER_FOOTER_LINKS, type HelpCenterFooterLinks } from '@/utils/help-center-links';
import { icon } from '@/utils/icons';

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

@customElement('help-agent-composer')
export class HelpAgentComposer extends BaseHTMLElement {
  private _connectionStatus: ConnectionStatus = 'disconnected';
  private _composerLocked = false;
  private _footerLinks: HelpCenterFooterLinks = DEFAULT_HELP_CENTER_FOOTER_LINKS;
  private _attachments: readonly HelpAgentAttachmentPreview[] = [];
  private _attachmentAccept = '';
  private _attachmentsEnabled = false;
  private _draftText = '';

  init(): void {
    super.init();
    this.render();
  }

  setConnectionStatus(status: ConnectionStatus): void {
    this._connectionStatus = status;
    this.updateControls();
  }

  set composerLocked(locked: boolean) {
    const nextValue = Boolean(locked);
    if (this._composerLocked === nextValue) {
      return;
    }

    this._composerLocked = nextValue;
    if (this.initialized) {
      this.updateControls();
    }
  }

  get composerLocked(): boolean {
    return this._composerLocked;
  }

  set footerLinks(links: HelpCenterFooterLinks | null | undefined) {
    const nextLinks = links ?? DEFAULT_HELP_CENTER_FOOTER_LINKS;
    const didChange = this._footerLinks.releaseNotes !== nextLinks.releaseNotes
      || this._footerLinks.contact !== nextLinks.contact;

    this._footerLinks = { ...nextLinks };

    if (didChange && this.initialized) {
      this.render();
    }
  }

  get footerLinks(): HelpCenterFooterLinks {
    return { ...this._footerLinks };
  }

  set attachments(nextAttachments: readonly HelpAgentAttachmentPreview[] | null | undefined) {
    const normalizedAttachments = nextAttachments ? [...nextAttachments] : [];
    const didChange = this._attachments.length !== normalizedAttachments.length
      || this._attachments.some((attachment, index) => {
        const nextAttachment = normalizedAttachments[index];
        return !nextAttachment
          || attachment.id !== nextAttachment.id
          || attachment.previewUrl !== nextAttachment.previewUrl
          || attachment.name !== nextAttachment.name;
      });

    this._attachments = normalizedAttachments;

    if (didChange && this.initialized) {
      this.render();
    }
  }

  get attachments(): readonly HelpAgentAttachmentPreview[] {
    return this._attachments;
  }

  set attachmentAccept(accept: string | null | undefined) {
    this._attachmentAccept = accept?.trim() ?? '';

    if (this.initialized) {
      const input = first(this, '[data-role="file-input"]') as HTMLInputElement | null;
      if (input) {
        input.accept = this._attachmentAccept;
      }
    }
  }

  get attachmentAccept(): string {
    return this._attachmentAccept;
  }

  set attachmentsEnabled(enabled: boolean) {
    const nextValue = Boolean(enabled);
    if (this._attachmentsEnabled === nextValue) {
      return;
    }

    this._attachmentsEnabled = nextValue;
    if (this.initialized) {
      this.render();
    }
  }

  get attachmentsEnabled(): boolean {
    return this._attachmentsEnabled;
  }

  focusInput(): void {
    (first(this, 'textarea') as HTMLTextAreaElement | null)?.focus();
  }

  clearDraft(): void {
    this._draftText = '';
    const textarea = first(this, 'textarea') as HTMLTextAreaElement | null;
    if (!textarea) {
      return;
    }

    textarea.value = '';
    this.autoResize();
    this.updateControls();
  }

  openFilePicker(): void {
    if (!this._attachmentsEnabled || this._composerLocked) {
      return;
    }

    (first(this, '[data-role="file-input"]') as HTMLInputElement | null)?.click();
  }

  private render(): void {
    this.innerHTML = `
      <div class="help-agent-composer-shell" data-test="help-widget-composer">
        ${this._attachmentsEnabled ? `
          <input
            class="help-agent-composer-file-input"
            type="file"
            data-role="file-input"
            data-test="help-widget-file-input"
            multiple
            ${this._attachmentAccept ? `accept="${escapeAttribute(this._attachmentAccept)}"` : ''}
          />
        ` : ''}
        <div
          class="help-agent-composer-previews"
          data-test="help-widget-attachment-previews"
          ${this._attachments.length === 0 ? 'hidden' : ''}
          role="region"
          aria-label="Selected attachments"
        >
          ${this.renderAttachments()}
        </div>
        <div class="help-agent-composer-row" data-test="help-widget-composer-row">
          <div class="help-agent-composer-field" data-test="help-widget-composer-field">
            <div class="help-agent-composer-input" data-test="help-widget-composer-input">
              <textarea
                rows="1"
                placeholder="Enter a prompt here"
                aria-label="Message"
                data-test="help-widget-input"
                autocomplete="off"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
              >${escapeHtml(this._draftText)}</textarea>
              ${this._attachmentsEnabled ? `
                <button
                  type="button"
                  class="help-agent-composer-attachment"
                  data-action="attachment"
                  data-test="help-widget-attachment-button"
                  aria-label="Attach file"
                >
                  ${icon('attachment', { height: 'var(--ha-composer-attachment-icon-height)' })}
                </button>
              ` : ''}
            </div>
          </div>
          <button
            type="button"
            class="help-agent-composer-send"
            data-action="send"
            data-test="help-widget-send-button"
            aria-label="Send message"
          >
            ${icon('send', { height: 'var(--ha-composer-send-icon-height)' })}
          </button>
        </div>
        <nav class="help-agent-composer-links" data-test="help-widget-footer-links" aria-label="Help center links">
          <a
            class="help-agent-composer-link"
            data-test="help-widget-footer-link-release-notes"
            href="${escapeAttribute(this._footerLinks.releaseNotes)}"
            target="_blank"
            rel="noreferrer noopener"
          >
            ${icon('document-list', { size: 'var(--ha-footer-link-icon-size)' })}
            <span>Enlil Release Notes</span>
          </a>
          <a
            class="help-agent-composer-link"
            data-test="help-widget-footer-link-contact"
            href="${escapeAttribute(this._footerLinks.contact)}"
            target="_blank"
            rel="noreferrer noopener"
          >
            ${icon('contact-bubble', { size: 'var(--ha-footer-link-icon-size)' })}
            <span>Contact Us</span>
          </a>
        </nav>
      </div>
    `;

    this.autoResize();
    this.updateControls();
  }

  private renderAttachments(): string {
    if (this._attachments.length === 0) {
      return '';
    }

    return `
      <div class="help-agent-composer-preview-list" data-test="help-widget-attachment-preview-list" role="list" aria-label="Selected attachments">
        ${this._attachments.map((attachment) => `
          <article
            class="help-agent-composer-preview-chip is-${attachment.kind}"
            data-test="help-widget-attachment-chip"
            data-test-key="${escapeAttribute(attachment.kind)}"
            role="listitem"
          >
            ${attachment.kind === 'image' && attachment.previewUrl
              ? `
                <button
                  type="button"
                  class="help-agent-composer-preview-open"
                  data-action="open-attachment-preview"
                  data-test="help-widget-attachment-preview-open"
                  data-attachment-id="${escapeAttribute(attachment.id)}"
                  aria-label="Preview image ${escapeAttribute(attachment.name)}"
                >
                  <span class="help-agent-composer-preview-thumb" aria-hidden="true">
                    <img src="${escapeAttribute(attachment.previewUrl)}" alt="" loading="lazy" />
                  </span>
                  <span class="help-agent-composer-preview-label" title="${escapeAttribute(attachment.name)}">
                    ${escapeHtml(attachment.name)}
                  </span>
                </button>
              `
              : `
                <div class="help-agent-composer-preview-content" aria-label="Attached file ${escapeAttribute(attachment.name)}">
                  <span class="help-agent-composer-preview-thumb" aria-hidden="true">
                    ${icon('attachment', { height: 'var(--ha-composer-preview-file-icon-height)' })}
                  </span>
                  <span class="help-agent-composer-preview-label" title="${escapeAttribute(attachment.name)}">
                    ${escapeHtml(attachment.name)}
                  </span>
                </div>
              `}
            <button
              type="button"
              class="help-agent-composer-preview-remove"
              data-action="remove-attachment"
              data-test="help-widget-attachment-remove"
              data-attachment-id="${escapeAttribute(attachment.id)}"
              aria-label="Remove ${escapeAttribute(attachment.name)}"
            >
              ${icon('close', { width: 'var(--ha-composer-preview-remove-icon-size)', height: 'var(--ha-composer-preview-remove-icon-size)' })}
            </button>
          </article>
        `).join('')}
      </div>
    `;
  }

  private updateControls(): void {
    const textarea = first(this, 'textarea') as HTMLTextAreaElement | null;
    const sendButton = first(this, '[data-action="send"]') as HTMLButtonElement | null;
    const attachmentButton = first(this, '[data-action="attachment"]') as HTMLButtonElement | null;
    const fileInput = first(this, '[data-role="file-input"]') as HTMLInputElement | null;

    if (!textarea || !sendButton) {
      return;
    }

    const isConnected = this._connectionStatus === 'connected';
    const hasDraftPayload = textarea.value.trim().length > 0 || this._attachments.length > 0;
    const isDisabled = !isConnected || this._composerLocked || !hasDraftPayload;
    sendButton.disabled = isDisabled;
    textarea.setAttribute('aria-disabled', String(!isConnected || this._composerLocked));
    if (attachmentButton) {
      attachmentButton.disabled = this._composerLocked;
    }
    if (fileInput) {
      fileInput.disabled = this._composerLocked;
    }

    for (const removeButton of this.querySelectorAll('[data-action="remove-attachment"]')) {
      (removeButton as HTMLButtonElement).disabled = this._composerLocked;
    }
  }

  private autoResize(): void {
    const textarea = first(this, 'textarea') as HTMLTextAreaElement | null;
    if (!textarea) {
      return;
    }

    const maxHeight = 200;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private emitSend(): void {
    if (this._connectionStatus !== 'connected' || this._composerLocked) {
      return;
    }

    const textarea = first(this, 'textarea') as HTMLTextAreaElement | null;
    if (!textarea) {
      return;
    }

    const rawText = textarea.value;
    const text = rawText.trim();
    if (!text && this._attachments.length === 0) {
      return;
    }

    this._draftText = rawText;
    this.dispatchEvent(new CustomEvent('help-agent:send', {
      bubbles: true,
      composed: true,
      detail: { text },
    }));
  }

  @onEvent('input', 'textarea')
  onInput(event: Event & { selectTarget: HTMLTextAreaElement }): void {
    this._draftText = event.selectTarget.value;
    this.autoResize();
    this.updateControls();
  }

  @onEvent('keydown', 'textarea')
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this._composerLocked) {
        return;
      }
      this.emitSend();
    }
  }

  @onEvent('click', '[data-action="send"]')
  onSendClick(): void {
    this.emitSend();
  }

  @onEvent('click', '[data-action="attachment"]')
  onAttachmentClick(): void {
    this.openFilePicker();
  }

  @onEvent('change', '[data-role="file-input"]')
  onFileInputChange(event: Event & { selectTarget: HTMLInputElement }): void {
    const input = event.selectTarget;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    input.value = '';
    if (this._composerLocked) {
      return;
    }

    this.dispatchEvent(new CustomEvent('help-agent:files-selected', {
      bubbles: true,
      composed: true,
      detail: { files },
    }));
  }

  @onEvent('click', '[data-action="remove-attachment"]')
  onRemoveAttachment(event: Event & { selectTarget: HTMLElement }): void {
    if (this._composerLocked) {
      return;
    }

    const attachmentId = event.selectTarget.getAttribute('data-attachment-id');
    if (!attachmentId) {
      return;
    }

    this.dispatchEvent(new CustomEvent('help-agent:remove-attachment', {
      bubbles: true,
      composed: true,
      detail: { id: attachmentId },
    }));
  }

  @onEvent('click', '[data-action="open-attachment-preview"]')
  onOpenAttachmentPreview(event: Event & { selectTarget: HTMLElement }): void {
    const attachmentId = event.selectTarget.getAttribute('data-attachment-id');
    if (!attachmentId) {
      return;
    }

    const imageAttachments = this._attachments.filter(
      (attachment) => attachment.kind === 'image' && Boolean(attachment.previewUrl),
    );
    const startIndex = imageAttachments.findIndex((attachment) => attachment.id === attachmentId);

    if (startIndex === -1) {
      return;
    }

    const images: ChatImage[] = imageAttachments.map((attachment) => ({
      url: attachment.previewUrl || '',
      caption: attachment.name,
    }));

    this.dispatchEvent(new CustomEvent('help-agent:open-gallery', {
      bubbles: true,
      composed: true,
      detail: {
        images,
        startIndex,
      },
    }));
  }
}
